<template>
  <div class="dashboard-container">
    <!-- SIDEBAR -->
    <Sidebar @open-contacts="goToEmailContacts" />

    <!-- MAIN -->
    <main class="main properties-design">
      <!-- TOPBAR -->
      <Topbar />

      <!-- CONTENT -->
      <div class="screen">
        <div class="page-header-row">
          <div class="page-header-actions">
            <button class="btn btn-outline btn-sm future" disabled title="Coming soon">Export <span class="future-tag">future</span></button>
            <button class="btn btn-primary future" disabled title="Coming soon">
              <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add property <span class="future-tag">future</span>
            </button>
          </div>
        </div>

        <!-- STAT STRIP -->
        <div class="stat-strip">
          <div class="stat-card"><div class="stat-label">Total properties</div><div class="stat-value">{{ stats.total }}</div><div class="stat-sub">across all owners</div></div>
          <div class="stat-card"><div class="stat-label">Owners complete</div><div class="stat-value green">{{ stats.complete }}</div><div class="stat-sub">of {{ stats.total }} properties</div></div>
          <div class="stat-card"><div class="stat-label">Pending setup</div><div class="stat-value amber">{{ stats.incomplete }}</div><div class="stat-sub">details still needed</div></div>
          <div class="stat-card"><div class="stat-label">Action needed</div><div class="stat-value red">{{ stats.action_needed }}</div><div class="stat-sub">incomplete details</div></div>
        </div>

        <!-- TOOLBAR -->
        <div class="toolbar">
          <div class="toolbar-left">
            <div class="search-bar">
              <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <input type="text" placeholder="Search properties or owners…" v-model="search"/>
            </div>
            <div class="filter-tab" :class="{active: filter==='all'}" @click="filter='all'">All</div>
            <div class="filter-tab" :class="{active: filter==='pending'}" @click="filter='pending'">Pending</div>
            <div class="filter-tab" :class="{active: filter==='action'}" @click="filter='action'">Action needed</div>
            <div class="filter-tab" :class="{active: filter==='complete'}" @click="filter='complete'">Complete</div>
          </div>
        </div>

        <!-- LOADING -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading properties…</p>
        </div>

        <!-- EMPTY -->
        <div v-else-if="!properties.length" class="empty-state">
          <div class="empty-icon">🏨</div>
          <h3>No properties found</h3>
          <p>No properties are currently configured in your PMS.</p>
        </div>

        <!-- TABLE -->
        <div v-else class="card" style="margin-bottom:0;">
          <table>
            <thead><tr>
              <th style="width:28%">Property</th>
              <th style="width:22%">Owner</th>
              <th style="width:9%">Commission</th>
              <th style="width:16%">Owner status</th>
              <th style="width:14%">DAC7 status</th>
              <th style="width:11%"></th>
            </tr></thead>
            <tbody>
              <tr v-for="p in filtered" :key="p.id" @click="openDac7(p)">
                <td>
                  <div class="prop-cell">
                    <div class="prop-avatar" :style="avatarStyle(p)">{{ initials(p.name) }}</div>
                    <div>
                      <div class="prop-name-text">{{ p.name }}</div>
                      <div class="prop-address-text">{{ p.address || '—' }}</div>
                    </div>
                  </div>
                </td>
                <td class="td-pad">
                  <div class="owner-name-text">{{ p.owner_name || '—' }}</div>
                  <div class="owner-email-text">{{ p.owner_email || 'No owner set' }}</div>
                </td>
                <td class="td-pad"><span class="commission-text">{{ p.commission ? p.commission + '%' : '—' }}</span></td>
                <td class="td-pad"><span class="pill" :class="ownerPillClass(p)">{{ ownerPillLabel(p) }}</span></td>
                <td class="td-pad"><span class="pill" :class="dac7PillClass(p.dac7_status)">{{ dac7PillLabel(p.dac7_status) }}</span></td>
                <td class="action-cell">
                  <span class="btn-row">View →</span>
                </td>
              </tr>
              <tr v-if="!filtered.length">
                <td colspan="6" style="padding:22px;text-align:center;color:var(--text-muted);font-size:13px;">No properties match this filter.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- DAC7 EDIT DRAWER -->
      <Dac7EditDrawer
        v-if="dac7Open"
        :property="dac7Target"
        @close="onDrawerClose"
        @saved="onOwnerSaved"
      />
    </main>
  </div>
</template>

<script>
import axios from 'axios'
import SETTINGS from '@/config/settings.js'
import Sidebar from '@/components/Sidebar.vue'
import Topbar from '@/components/Topbar.vue'
import Dac7EditDrawer from '@/components/properties/Dac7EditDrawer.vue'
import propertiesApi from '@/services/properties.js'

export default {
  name: 'PropertiesView',
  components: { Sidebar, Topbar, Dac7EditDrawer },
  data() {
    return {
      loading: false,
      properties: [],
      dac7Map: {},
      stats: { total: 0, complete: 0, incomplete: 0, action_needed: 0 },
      search: '',
      filter: 'all',
      dac7Open: false,
      dac7Target: null,
      baseUrl: SETTINGS.API.baseUrl,
    }
  },
  computed: {
    filtered() {
      const q = (this.search || '').toLowerCase()
      return this.properties.filter((p) => {
        if (q) {
          const hay = `${p.name} ${p.owner_name || ''} ${p.owner_email || ''} ${p.address || ''}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        if (this.filter === 'all') return true
        if (this.filter === 'complete') return p.dac7_status === 'complete'
        if (this.filter === 'action') return p.dac7_status === 'action_needed'
        if (this.filter === 'pending') return !['complete', 'action_needed'].includes(p.dac7_status)
        return true
      })
    },
  },
  async mounted() {
    await this.loadAll()
  },
  methods: {
    async loadAll() {
      this.loading = true
      try {
        const token = localStorage.getItem('token')
        const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig') || 'null')
        if (!selectedPms || !selectedPms.id) {
          this.properties = []
          this.stats = { total: 0, complete: 0, incomplete: 0, action_needed: 0 }
          this.loading = false
          return
        }
        const [propsRes, summary] = await Promise.all([
          axios.get(`${this.baseUrl}/api/pms/properties/`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { pms_config_id: selectedPms.id },
          }),
          propertiesApi.dac7Summary(),
        ])

        const pmsProps = (propsRes.data && propsRes.data.properties && propsRes.data.properties.data) || []
        const byProp = (summary && summary.data && summary.data.by_property) || []
        const commissionMap = (summary && summary.data && summary.data.commission_by_property) || {}
        const detailsMap = (summary && summary.data && summary.data.details_by_property) || {}
        const map = {}
        byProp.forEach((o) => { map[String(o.property_id)] = o })
        this.dac7Map = map
        this.stats = {
          total: summary.data.total || pmsProps.length,
          complete: summary.data.complete || 0,
          incomplete: summary.data.incomplete || 0,
          action_needed: summary.data.action_needed || 0,
        }

        this.properties = pmsProps.map((raw) => {
          const key = String(raw.id)
          const owner = map[key] || {}
          const override = detailsMap[key] || {}
          const address_line_1 = override.address_line_1 || raw.address || ''
          const city = override.city || raw.city || ''
          const postal_code = override.postal_code || raw.postalCode || ''
          const addressParts = [address_line_1, override.address_line_2, city, postal_code].filter(Boolean)
          return {
            id: raw.id,
            name: raw.name,
            address: addressParts.join(', '),
            address_line_1,
            city,
            postal_code,
            pms_address_line_1: raw.address || '',
            pms_city: raw.city || '',
            pms_postal_code: raw.postalCode || '',
            owner_name: owner.owner_name || '',
            owner_email: owner.owner_email || '',
            dac7_status: owner.dac7_status || 'details_needed',
            owner_status: owner.owner_status || (owner.owner_email ? 'pending' : 'not_set'),
            commission: owner.commission != null ? owner.commission : (commissionMap[key] != null ? commissionMap[key] : null),
          }
        })

        // If no owners created yet, stats.total should still reflect PMS properties.
        if (!this.stats.total) this.stats.total = this.properties.length
      } catch (e) {
        console.error('Failed to load properties', e)
      } finally {
        this.loading = false
      }
    },
    initials(name) {
      if (!name) return '·'
      return name
        .split(/\s+/)
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    },
    avatarStyle(p) {
      if (p.dac7_status === 'complete') return 'background:rgba(0,184,148,0.1);color:#00b894;'
      if (p.dac7_status === 'action_needed') return 'background:rgba(214,48,49,0.08);color:#d63031;'
      if (p.dac7_status === 'incomplete') return 'background:rgba(253,203,110,0.2);color:#b7791f;'
      return ''
    },
    ownerPillLabel(p) {
      if (p.owner_status === 'not_set') return 'Not set'
      if (p.owner_status === 'complete') return 'Complete'
      return 'Pending setup'
    },
    ownerPillClass(p) {
      if (p.owner_status === 'not_set') return 'pill-grey'
      if (p.owner_status === 'complete') return 'pill-green'
      return 'pill-amber'
    },
    dac7PillLabel(s) {
      if (s === 'complete') return 'Complete ✓'
      if (s === 'action_needed') return 'Action needed'
      if (s === 'incomplete') return 'Incomplete'
      return 'Details needed'
    },
    dac7PillClass(s) {
      if (s === 'complete') return 'pill-green'
      if (s === 'action_needed') return 'pill-red'
      if (s === 'incomplete') return 'pill-amber'
      return 'pill-grey'
    },
    openDac7(p) {
      this.dac7Target = p
      this.dac7Open = true
    },
    onDrawerClose() {
      this.dac7Open = false
      this.dac7Target = null
    },
    async onOwnerSaved() {
      await this.loadAll()
      this.dac7Open = false
      this.dac7Target = null
    },
    goToEmailContacts() {
      this.$router.push({ path: '/settings', query: { tab: 'reports' } })
    },
  },
}
</script>

<style scoped>
.dashboard-container { display: flex; min-height: 100vh; }
.main { margin-left: 240px; min-height: 100vh; display: flex; flex-direction: column; flex: 1; }
.page-header-row { display: flex; align-items: center; justify-content: flex-end; gap: 16px; margin-bottom: 18px; flex-wrap: wrap; }
.page-header-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.page-header-actions .btn svg { width: 14px; height: 14px; stroke: currentColor; stroke-width: 2.5; fill: none; stroke-linecap: round; stroke-linejoin: round; margin-right: 4px; vertical-align: middle; }
@media (max-width: 900px) {
  .main { margin-left: 0; }
}

.properties-design {
  --accent: #5b4fcf;
  --accent-dim: rgba(91,79,207,0.08);
  --accent-border: rgba(91,79,207,0.25);
  --bg: #f5f4fb;
  --white: #ffffff;
  --text: #1a1a2e;
  --text-mid: #4a4880;
  --text-muted: #9896bb;
  --border: #e8e6f4;
  --green: #00b894;
  --green-dim: rgba(0,184,148,0.1);
  --green-border: rgba(0,184,148,0.25);
  --amber: #fdcb6e;
  --amber-text: #b7791f;
  --amber-dim: rgba(253,203,110,0.2);
  --amber-border: rgba(253,203,110,0.5);
  --red: #d63031;
  --red-dim: rgba(214,48,49,0.08);
  --red-border: rgba(214,48,49,0.2);
  --gold: #f59e0b;
  --gold-dim: rgba(245,158,11,0.1);
  --gold-border: rgba(245,158,11,0.3);
  --shadow-sm: 0 1px 4px rgba(26,24,61,0.07);
  --radius: 10px;
  font-family: 'Inter', sans-serif;
  color: var(--text);
  background: var(--bg);
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
}
.properties-design *, .properties-design *::before, .properties-design *::after { box-sizing: border-box; }

.screen { padding: 24px 28px; }

.stat-strip { display: grid; grid-template-columns: repeat(4,1fr); gap: 12px; margin-bottom: 22px; }
.stat-card { background: var(--white); border: 1px solid var(--border); border-radius: var(--radius); padding: 14px 18px; box-shadow: var(--shadow-sm); }
.stat-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); margin-bottom: 6px; }
.stat-value { font-family: 'Outfit', sans-serif; font-size: 1.7rem; font-weight: 800; color: var(--text); line-height: 1; }
.stat-value.green { color: var(--green); }
.stat-value.amber { color: var(--amber-text); }
.stat-value.red { color: var(--red); }
.stat-sub { font-size: 11px; color: var(--text-muted); margin-top: 4px; }

.toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; gap: 12px; }
.toolbar-left { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.search-bar { display: flex; align-items: center; gap: 8px; background: var(--white); border: 1.5px solid var(--border); border-radius: 8px; padding: 7px 12px; }
.search-bar svg { width: 14px; height: 14px; fill: none; stroke: var(--text-muted); stroke-width: 2; }
.search-bar input { border: none; outline: none; font-size: 13px; color: var(--text); background: transparent; font-family: 'Inter', sans-serif; width: 220px; }
.search-bar input::placeholder { color: var(--text-muted); }
.filter-tab { padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; cursor: pointer; border: 1.5px solid var(--border); background: var(--white); color: var(--text-muted); transition: all 0.15s; white-space: nowrap; }
.filter-tab.active { background: var(--accent); color: #fff; border-color: var(--accent); }
.filter-tab:hover:not(.active) { border-color: var(--accent); color: var(--accent); }

.card { background: var(--white); border-radius: var(--radius); border: 1px solid var(--border); box-shadow: var(--shadow-sm); overflow: hidden; margin-bottom: 14px; }

table { width: 100%; border-collapse: collapse; }
thead th { padding: 10px 16px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); border-bottom: 1px solid var(--border); text-align: left; background: var(--bg); white-space: nowrap; }
tbody td { padding: 0; border-bottom: 1px solid #f0eef8; vertical-align: middle; }
tbody tr:last-child td { border-bottom: none; }
tbody tr { cursor: pointer; transition: background 0.1s; }
tbody tr:hover td { background: #faf9ff; }
.prop-cell { display: flex; align-items: center; gap: 12px; padding: 14px 16px; }
.prop-avatar { width: 34px; height: 34px; border-radius: 8px; background: var(--accent-dim); display: flex; align-items: center; justify-content: center; font-family: 'Outfit', sans-serif; font-size: 11px; font-weight: 800; color: var(--accent); flex-shrink: 0; }
.prop-name-text { font-weight: 600; font-size: 13px; color: var(--text); }
.prop-address-text { font-size: 11px; color: var(--text-muted); margin-top: 1px; }
.td-pad { padding: 14px 16px; font-size: 13px; }
.owner-name-text { font-size: 13px; color: var(--text); font-weight: 500; }
.owner-email-text { font-size: 11px; color: var(--text-muted); margin-top: 1px; }
.commission-text { font-family: 'Outfit', sans-serif; font-size: 0.95rem; font-weight: 700; color: var(--text); }
.action-cell { padding: 14px 16px; text-align: right; }
.btn-row { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 7px; font-size: 12px; font-weight: 600; color: var(--text-mid); background: var(--bg); border: 1.5px solid var(--border); font-family: 'Inter', sans-serif; white-space: nowrap; }
tbody tr:hover .btn-row { border-color: var(--accent); color: var(--accent); background: var(--accent-dim); }

.pill { display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 600; white-space: nowrap; }
.pill::before { content: ''; width: 5px; height: 5px; border-radius: 50%; background: currentColor; opacity: 0.7; }
.pill-green { background: var(--green-dim); color: var(--green); border: 1px solid var(--green-border); }
.pill-amber { background: var(--amber-dim); color: var(--amber-text); border: 1px solid var(--amber-border); }
.pill-red { background: var(--red-dim); color: var(--red); border: 1px solid var(--red-border); }
.pill-grey { background: var(--bg); color: var(--text-muted); border: 1px solid var(--border); }

.btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; font-family: 'Inter', sans-serif; transition: opacity 0.15s, transform 0.1s; white-space: nowrap; }
.btn:hover:not(:disabled) { opacity: 0.88; }
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-primary { background: var(--accent); color: #fff; }
.btn-outline { background: transparent; color: var(--text-mid); border: 1.5px solid var(--border); }
.btn-sm { padding: 5px 11px; font-size: 12px; }
.btn svg { width: 13px; height: 13px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; }
.future-tag { font-size: 9px; font-weight: 700; padding: 1px 6px; border-radius: 20px; background: var(--gold-dim); color: #92400e; border: 1px solid var(--gold-border); text-transform: uppercase; letter-spacing: 0.06em; margin-left: 4px; }

.loading-state { text-align: center; padding: 60px 20px; color: var(--text-muted); font-size: 13px; }
.spinner { width: 32px; height: 32px; border: 3px solid var(--border); border-top-color: var(--accent); border-radius: 50%; margin: 0 auto 12px; animation: spin 0.9s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.empty-state { text-align: center; padding: 60px 20px; background: var(--white); border: 1px solid var(--border); border-radius: var(--radius); }
.empty-icon { font-size: 40px; margin-bottom: 8px; }
.empty-state h3 { font-family: 'Outfit', sans-serif; font-size: 1rem; font-weight: 700; color: var(--text); margin-bottom: 4px; }
.empty-state p { font-size: 13px; color: var(--text-muted); }

@media (max-width: 900px) {
  .stat-strip { grid-template-columns: repeat(2,1fr); }
}
</style>
